/**
 * Railway-style time range filter with quick range buttons,
 * relative time input ("5m ago"), and custom from/until fields.
 */

import { useState, useCallback } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Clock, ChevronDown } from 'lucide-react'

// --- Types ---

export interface TimeRange {
  readonly from: Date
  readonly until: Date
  readonly label: string
}

interface QuickRange {
  readonly label: string
  readonly value: string
  readonly getRange: () => TimeRange
}

// --- Quick Range Presets ---

function minutesAgo(minutes: number): Date {
  return new Date(Date.now() - minutes * 60 * 1000)
}

function hoursAgo(hours: number): Date {
  return new Date(Date.now() - hours * 60 * 60 * 1000)
}

function daysAgo(days: number): Date {
  return new Date(Date.now() - days * 24 * 60 * 60 * 1000)
}

const QUICK_RANGES: QuickRange[] = [
  { label: '5 min', value: '5m', getRange: () => ({ from: minutesAgo(5), until: new Date(), label: 'Last 5 min' }) },
  { label: '15 min', value: '15m', getRange: () => ({ from: minutesAgo(15), until: new Date(), label: 'Last 15 min' }) },
  { label: '30 min', value: '30m', getRange: () => ({ from: minutesAgo(30), until: new Date(), label: 'Last 30 min' }) },
  { label: '1 hour', value: '1h', getRange: () => ({ from: hoursAgo(1), until: new Date(), label: 'Last 1 hour' }) },
  { label: '3 hour', value: '3h', getRange: () => ({ from: hoursAgo(3), until: new Date(), label: 'Last 3 hours' }) },
  { label: '6 hour', value: '6h', getRange: () => ({ from: hoursAgo(6), until: new Date(), label: 'Last 6 hours' }) },
  { label: '1 day', value: '1d', getRange: () => ({ from: daysAgo(1), until: new Date(), label: 'Last 1 day' }) },
  { label: '7 day', value: '7d', getRange: () => ({ from: daysAgo(7), until: new Date(), label: 'Last 7 days' }) },
  { label: '30 day', value: '30d', getRange: () => ({ from: daysAgo(30), until: new Date(), label: 'Last 30 days' }) },
]

// --- Parse relative time input ---

function parseRelativeTime(input: string): Date | null {
  const trimmed = input.trim().toLowerCase()

  // "now"
  if (trimmed === 'now') return new Date()

  // Bare number: "5" → 5 minutes ago
  if (/^\d+$/.test(trimmed)) {
    return minutesAgo(parseInt(trimmed, 10))
  }

  // Short form without "ago": "5m", "2h", "3d"
  const shortMatch = trimmed.match(/^(\d+)\s*(m|min|h|hr|hour|d|day)s?$/i)
  if (shortMatch) {
    const amount = parseInt(shortMatch[1], 10)
    const unit = shortMatch[2].charAt(0)
    if (unit === 'm') return minutesAgo(amount)
    if (unit === 'h') return hoursAgo(amount)
    if (unit === 'd') return daysAgo(amount)
  }

  // Full form with "ago": "5m ago", "2h ago", "3d ago"
  const relMatch = trimmed.match(/^(\d+)\s*(m|min|h|hr|hour|d|day)s?\s*ago$/i)
  if (relMatch) {
    const amount = parseInt(relMatch[1], 10)
    const unit = relMatch[2].charAt(0)
    if (unit === 'm') return minutesAgo(amount)
    if (unit === 'h') return hoursAgo(amount)
    if (unit === 'd') return daysAgo(amount)
  }

  // ISO date: "2026-04-03"
  const isoMatch = trimmed.match(/^\d{4}-\d{2}-\d{2}/)
  if (isoMatch) {
    const parsed = new Date(trimmed)
    if (!isNaN(parsed.getTime())) return parsed
  }

  return null
}

function formatTimeLabel(date: Date): string {
  const diffMs = Date.now() - date.getTime()
  if (diffMs < 0) return 'now'

  const minutes = Math.floor(diffMs / 60000)
  if (minutes < 60) return `${minutes}m ago`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`

  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// --- Component ---

interface Props {
  readonly value: TimeRange
  readonly onChange: (range: TimeRange) => void
}

export function TimeRangeFilter({ value, onChange }: Props) {
  const [open, setOpen] = useState(false)
  const [activeQuick, setActiveQuick] = useState('1d')
  const [fromInput, setFromInput] = useState('')
  const [untilInput, setUntilInput] = useState('')

  const handleQuickRange = useCallback((quick: QuickRange) => {
    setActiveQuick(quick.value)
    onChange(quick.getRange())
    setFromInput('')
    setUntilInput('')
    setOpen(false)
  }, [onChange])

  const handleCustomApply = useCallback(() => {
    const from = parseRelativeTime(fromInput)
    const until = parseRelativeTime(untilInput || 'now')
    if (from && until) {
      setActiveQuick('')
      onChange({
        from,
        until,
        label: `${formatTimeLabel(from)} — ${untilInput || 'now'}`,
      })
      setOpen(false)
    }
  }, [fromInput, untilInput, onChange])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Clock className="h-3.5 w-3.5" />
          <span>{value.label}</span>
          <ChevronDown className="h-3.5 w-3.5 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4" align="end">
        {/* Quick Range Grid */}
        <p className="text-xs font-medium text-muted-foreground mb-2">Quick range</p>
        <div className="grid grid-cols-3 gap-1.5 mb-4">
          {QUICK_RANGES.map((quick) => (
            <Button
              key={quick.value}
              variant={activeQuick === quick.value ? 'default' : 'outline'}
              size="sm"
              className="text-xs h-8"
              onClick={() => handleQuickRange(quick)}
            >
              {quick.label}
            </Button>
          ))}
        </div>

        {/* Custom From/Until */}
        <div className="space-y-3 border-t pt-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground">From</label>
            <Input
              placeholder="5m ago, 2h ago, 2026-04-03"
              value={fromInput}
              onChange={(e) => setFromInput(e.target.value)}
              className="mt-1 text-sm h-9"
              onKeyDown={(e) => e.key === 'Enter' && handleCustomApply()}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Until</label>
            <Input
              placeholder="now"
              value={untilInput}
              onChange={(e) => setUntilInput(e.target.value)}
              className="mt-1 text-sm h-9"
              onKeyDown={(e) => e.key === 'Enter' && handleCustomApply()}
            />
          </div>
          <Button
            size="sm"
            className="w-full"
            onClick={handleCustomApply}
            disabled={!fromInput}
          >
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
